import type { ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { cn } from "@/lib/utils";

export function Page({ children, className, wide }: { children: ReactNode; className?: string; wide?: boolean }) {
  return <div className={cn("mx-auto w-full px-6 py-6", wide ? "max-w-[1680px]" : "max-w-[1400px]", className)}>{children}</div>;
}

export function PageSkeleton() {
  return (
    <Page>
      <Skeleton className="mb-2 h-6 w-48" />
      <Skeleton className="mb-6 h-4 w-96" />
      <div className="grid grid-cols-4 gap-3">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
      <Skeleton className="mt-4 h-72" />
    </Page>
  );
}

export function ErrorPanel({ error, reset, className }: { error: unknown; reset?: () => void; className?: string }) {
  const e = error as Error;
  const notImpl = e instanceof ApiError && e.status === 501;
  return (
    <div className={cn("flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center", className)}>
      <AlertTriangle className="size-6 text-warning" />
      <p className="text-sm font-medium">{notImpl ? "Not available in live mode yet" : "Something went wrong"}</p>
      <p className="max-w-md text-xs text-fg-3">
        {e?.message ?? "Unknown error"}
        {e instanceof ApiError && e.requestId ? <span className="mt-1 block font-mono">request {e.requestId}</span> : null}
      </p>
      {reset ? (
        <Button variant="outline" size="sm" onClick={reset} className="mt-2">
          <RefreshCw /> Retry
        </Button>
      ) : null}
    </div>
  );
}

/** Renders loading / error / data for a query result in a consistent way. */
export function QueryState<T>({ query, children, loading }: { query: { data: T | undefined; isPending: boolean; error: unknown; refetch: () => unknown }; children: (data: T) => ReactNode; loading?: ReactNode }) {
  if (query.error) return <ErrorPanel error={query.error} reset={() => void query.refetch()} />;
  if (query.isPending || query.data === undefined) return <>{loading ?? <Skeleton className="h-40 w-full" />}</>;
  return <>{children(query.data)}</>;
}
